import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, num, fmtDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileSpreadsheet, Landmark, ArrowRight, FileText, Trash2, Eye, Files, BarChart3, Coins } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-border bg-white p-5 transition-shadow duration-200 hover:shadow-sm"
       data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="num mt-3 font-display text-3xl font-extrabold tracking-tight">{value}</p>
  </div>
);

const statusColor = (s) =>
  ({ Completed: "bg-ok text-ok-foreground border-green-200",
     "Needs Review": "bg-warn text-warn-foreground border-warn-border",
     Failed: "bg-red-50 text-red-700 border-red-200",
     Processing: "bg-secondary text-secondary-foreground border-blue-200",
     Waiting: "bg-muted text-muted-foreground border-border" }[s] || "bg-muted text-muted-foreground");

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/stats/dashboard");
      setStats(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      load(); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Dashboard</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
          Welcome back, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {stats?.plan || user?.plan} plan · {stats?.credits_remaining ?? user?.credits} credits remaining
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-white p-7 transition-transform duration-200 hover:-translate-y-0.5">
          <FileSpreadsheet className="h-6 w-6 text-primary" />
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">Image/PDF → Excel</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Convert tables, invoices, lists and documents into editable Excel spreadsheets.
          </p>
          <Button className="mt-6 rounded-full" onClick={() => nav("/image-to-excel")}
                  data-testid="start-conversion-btn">
            Start Conversion <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="rounded-lg border border-border bg-white p-7 transition-transform duration-200 hover:-translate-y-0.5">
          <Landmark className="h-6 w-6 text-primary" />
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">Bank Statement Analyzer</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Extract transactions, analyze cash flow and generate a financial assessment.
          </p>
          <Button className="mt-6 rounded-full" onClick={() => nav("/analyzer")} data-testid="analyze-statement-btn">
            Analyze Statement <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Files} label="Documents Processed" value={num(stats?.documents_processed ?? 0)} testid="stat-documents" />
        <StatCard icon={Landmark} label="Statements Analyzed" value={num(stats?.statements_analyzed ?? 0)} testid="stat-statements" />
        <StatCard icon={BarChart3} label="Total Transactions" value={num(stats?.total_transactions ?? 0)} testid="stat-transactions" />
        <StatCard icon={Coins} label="Credits Remaining" value={num(stats?.credits_remaining ?? 0)} testid="stat-credits" />
      </div>

      <div className="rounded-lg border border-border bg-white">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="font-display text-xl font-semibold">Recent Documents</h3>
          <Link to="/documents" className="text-sm font-semibold text-primary" data-testid="view-all-documents-link">
            View all
          </Link>
        </div>
        {!stats?.recent_documents?.length ? (
          <div className="p-10 text-center" data-testid="empty-documents">
            <p className="text-sm text-muted-foreground">
              No documents yet. Start with the Bank Statement Analyzer or a demo statement.
            </p>
            <Button className="mt-4 rounded-full" onClick={() => nav("/analyzer")} data-testid="empty-cta-btn">
              Analyze Statement
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="recent-documents-table">
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Transactions</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_documents.map((d) => (
                  <TableRow key={d.doc_id} data-testid={`doc-row-${d.doc_id}`}>
                    <TableCell className="max-w-[240px] truncate font-medium">{d.filename}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {d.doc_type === "bank_statement" ? "Bank Statement" : "Image → Excel"}
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(d.uploaded_at)}</TableCell>
                    <TableCell><Badge variant="outline" className={statusColor(d.status)}>{d.status}</Badge></TableCell>
                    <TableCell className="num text-right">{num(d.transaction_count)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" title="View / Analyze"
                                onClick={() => nav(`/workspace/${d.job_id}`)} data-testid={`view-${d.doc_id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Download Excel"
                                onClick={() => downloadFile(`/export/excel/${d.job_id}`, "GrowRupee.xlsx")
                                  .then(() => toast.success("Excel downloaded"))
                                  .catch((e) => toast.error(errMsg(e)))}
                                data-testid={`excel-${d.doc_id}`}>
                          <FileSpreadsheet className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Generate Report"
                                onClick={() => downloadFile(`/export/pdf/${d.job_id}`, "GrowRupee_Report.pdf")
                                  .then(() => toast.success("Report generated"))
                                  .catch((e) => toast.error(errMsg(e)))}
                                data-testid={`pdf-${d.doc_id}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Delete" onClick={() => del(d.doc_id)}
                                data-testid={`delete-${d.doc_id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
